<script lang="ts">
export default {
  name: "FrontendNavigation",
};
</script>

<script lang="ts" setup>
import { type LocationQueryRaw, useRouter } from "vue-router";
import { useFleetRouteCheck } from "@/frontend/composables/useFleetRouteCheck";
import { useI18n } from "@/shared/composables/useI18n";
import AppNavigation from "@/shared/components/AppNavigation/index.vue";
import NavItem from "@/shared/components/AppNavigation/NavItem/index.vue";

import FleetNav from "./FleetNav/index.vue";
import FleetsNav from "./FleetsNav/index.vue";
import ToolsNav from "./ToolsNav/index.vue";
import VisualTestsNav from "./VisualTestsNav/index.vue";
import { useSessionStore } from "@/frontend/stores/session";
import { useHangarStore } from "@/frontend/stores/hangar";
import { useFiltersStore } from "@/shared/stores/filters";
import { storeToRefs } from "pinia";
import rsiLogo from "@/images/rsi_logo.png";
import favicon from "@/images/favicon-small.png";

const { t } = useI18n();

const sessionStore = useSessionStore();

const { isAuthenticated, currentUser } = storeToRefs(sessionStore);

const hangarStore = useHangarStore();

const { preview: hangarPreview } = storeToRefs(hangarStore);

const { isFleetRoute } = useFleetRouteCheck();

const filtersStore = useFiltersStore();

const { filters } = storeToRefs(filtersStore);

const route = useRoute();

const shouldVisualTestsRouteBeVisible = computed(() => {
  return process.env.NODE_ENV !== "production";
});

const isVisualTestsRoute = computed(() => {
  if (process.env.NODE_ENV === "production") {
    return false;
  }

  if (!route.name) {
    return false;
  }

  return String(route.name).includes("visual-tests");
});

const isShipRoute = computed(() => {
  if (!route.name) {
    return false;
  }

  return ["ships", "ship", "ship-images", "ship-videos"].includes(
    String(route.name),
  );
});

const isHangarRoute = computed(() => {
  if (!route.name) {
    return false;
  }

  return String(route.name).includes("hangar");
});

const filterFor = (routeName: string) => {
  if (!filters.value[routeName]) {
    return undefined;
  }

  return {
    q: filters.value[routeName],
  } as unknown as LocationQueryRaw;
};

const router = useRouter();

const logout = () => {
  void sessionStore.logout();
  void router.replace({ name: "login" });
};

const settingsActive = computed(() => {
  return [
    "settings-profile",
    "settings-account",
    "settings-hangar",
    "settings-features",
    "settings-notifications",
    "settings-security-status",
    "settings-two-factor-enable",
    "settings-two-factor-disable",
    "settings-two-factor-backup-codes",
    "settings-change-password",
    "settings-connections",
    "settings-oauth-applications",
    "settings-oauth-application",
    "settings-oauth-application-create",
    "settings-oauth-application-edit",
  ].includes(String(route.name));
});
</script>

<template>
  <AppNavigation :title="t('title.default')" :logo="favicon">
    <template #main>
      <VisualTestsNav v-if="isVisualTestsRoute" />
      <FleetNav v-else-if="isFleetRoute" />
      <template v-else>
        <NavItem
          :to="{ name: 'home' }"
          :label="t('nav.home')"
          icon="fa-duotone fa-home-alt"
          prefix="01"
        />
        <NavItem
          v-if="isAuthenticated || !hangarPreview"
          :to="{
            name: 'hangar',
            query: filterFor('hangar'),
          }"
          :label="t('nav.hangar.index')"
          :active="isHangarRoute"
          icon="fa-duotone fa-warehouse"
          prefix="02"
        />
        <NavItem
          v-else
          :to="{ name: 'hangar-preview' }"
          :label="t('nav.hangar.index')"
          icon="fa-light fa-warehouse"
          prefix="02"
        />
        <NavItem
          :to="{
            name: 'ships',
            query: filterFor('ships'),
          }"
          :label="t('nav.ships.index')"
          :active="isShipRoute"
          icon="fa-duotone fa-starship"
          prefix="03"
        />
        <NavItem
          :to="{ name: 'compare' }"
          :label="t('nav.compare.ships')"
          icon="fa-duotone fa-code-compare"
          prefix="04"
        />
        <FleetsNav />
        <NavItem
          :to="{ name: 'images' }"
          :label="t('nav.images')"
          icon="fa-duotone fa-images"
          prefix="06"
        />
        <ToolsNav />
        <NavItem
          :to="{ name: 'stats' }"
          :label="t('nav.stats')"
          icon="fa-duotone fa-chart-bar"
          prefix="08"
        />
        <template v-if="shouldVisualTestsRouteBeVisible">
          <li class="nav-item__divider" />
          <NavItem
            :to="{ name: 'visual-tests' }"
            :label="t('nav.visualTests.index')"
            icon="fa-duotone fa-pen-swirl"
            prefix="09"
          />
        </template>
      </template>
    </template>
    <template #footer>
      <template v-if="isAuthenticated && currentUser">
        <NavItem
          :to="{ name: 'settings' }"
          :active="settingsActive"
          :label="t('nav.settings.index')"
          icon="fa-light fa-cog"
        />
        <template v-if="currentUser.rsiHandle">
          <NavItem
            :href="`https://robertsspaceindustries.com/citizens/${currentUser.rsiHandle}`"
            :label="t('nav.rsiProfile')"
            :image="rsiLogo"
          />
        </template>
        <NavItem
          :action="logout"
          menu-key="logout"
          :label="t('nav.logout')"
          icon="fa-light fa-sign-out"
        />
        <NavItem
          menu-key="user-menu"
          :image="currentUser.avatar?.smallUrl"
          :avatar="true"
          :label="currentUser.username"
          class="user-menu mt-1"
          data-test="user-menu"
        />
      </template>
      <NavItem
        v-else
        key="user-menu-guest"
        :to="{ name: 'login' }"
        :label="t('nav.login')"
        icon="fa-light fa-sign-in"
      />
    </template>
  </AppNavigation>
</template>

<style lang="scss" scoped>
@import "index";
</style>
